/**
 * Status and category tones as complete Tailwind class strings.
 *
 * Source of truth: DESIGN.md ("Colour", category tones, ink mapping). Tailwind
 * v4 only generates classes that appear literally in the source, so every
 * value here is a full string; never compose one (`bg-${tone}`).
 *
 * - `text-` / `border-` / `ring-` utilities for primary, warning, success and
 *   destructive resolve to the matching `*-ink` token (tailwind.config.js);
 *   only `bg-*` keeps the pale fill. Bars are marks, so `bar` uses `bg-*-ink`
 *   to reach 3:1 in both themes.
 * - There is no `info` colour token: the info tone is built from accent and
 *   primary.
 * - Every flat fill carries a 1 px foreground outline (brand rule, WCAG 1.4.11).
 */

export type StatusTone = "success" | "warning" | "destructive" | "info"

export type StatusToneClasses = {
  /** Solid chip, badge or dot. */
  solid: string
  /** Soft panel or banner. */
  soft: string
  /** Icon or emphasis text on the page ground. */
  text: string
  /** Progress or meter bar fill: a mark, so it uses the ink and reaches 3:1 in both themes. */
  bar: string
}

export const STATUS_TONES: Record<StatusTone, StatusToneClasses> = {
  success: {
    solid: "bg-success text-success-foreground border border-foreground",
    soft: "bg-success/15 border border-success text-foreground",
    text: "text-success",
    bar: "bg-success-ink",
  },
  warning: {
    solid: "bg-warning text-warning-foreground border border-foreground",
    soft: "bg-warning/15 border border-warning text-foreground",
    text: "text-warning",
    bar: "bg-warning-ink",
  },
  destructive: {
    solid: "bg-destructive text-destructive-foreground border border-foreground",
    soft: "bg-destructive/15 border border-destructive text-foreground",
    text: "text-destructive",
    bar: "bg-destructive-ink",
  },
  info: {
    solid: "bg-accent text-accent-foreground border border-foreground",
    soft: "bg-primary/15 border border-primary text-foreground",
    text: "text-primary",
    bar: "bg-primary-ink",
  },
}

/**
 * Categorical fills (D5): brand accents that keep the same light value in both
 * themes, with charcoal text at 6.3:1 or better on each. `accent` and `chart-5`
 * are excluded because they turn dark in dark mode. A category colour never
 * carries identity alone: keep the icon and label on every categorised item.
 */
export const CATEGORY_TONES = [
  "bg-chart-1 text-chart-foreground border border-foreground",
  "bg-chart-2 text-chart-foreground border border-foreground",
  "bg-chart-3 text-chart-foreground border border-foreground",
  "bg-chart-4 text-chart-foreground border border-foreground",
  "bg-success text-chart-foreground border border-foreground",
] as const

/** Category tone for index `i`, wrapping (negative indices included). */
export function categoryTone(i: number): string {
  return CATEGORY_TONES[
    ((i % CATEGORY_TONES.length) + CATEGORY_TONES.length) % CATEGORY_TONES.length
  ]
}
